import { useEffect, useState } from "react";
import {
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type {
  CampaignTargetDraft,
  CampaignTaskDefinition,
  CampaignTaskKind,
  CampaignTaskTarget,
} from "../types";
import {
  getCampaignTaskTypeConfig,
  isConsolidatedKind,
} from "./campaignTaskTypes";
import {
  getTranslatedCampaignMetadataLabel,
  useCampaignTaskTypeChoices,
} from "./getTranslatedCampaignTaskLabel";
import { CampaignTargetInput } from "./CampaignTargetInput";
import {
  assertCreatedId,
  clearDefinitionTargets,
  loadCampaignTargets,
  replaceDefinitionTargets,
} from "./campaignTargets";

export const CampaignTaskDefinitionForm = ({
  campaignId,
  definitionId,
  initialTargets,
  onClose,
}: {
  campaignId: Identifier;
  definitionId?: Identifier;
  initialTargets?: CampaignTaskTarget[];
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const taskTypeChoices = useCampaignTaskTypeChoices(translate);
  const dataProvider = useDataProvider();
  const { data: existing } = useGetOne<CampaignTaskDefinition>(
    "campaign_task_definitions",
    { id: definitionId! },
    { enabled: definitionId != null },
  );
  const { data: salespeople } = useGetList("sales", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "first_name", order: "ASC" },
  });
  const { data: campaignTargets } = useGetList("campaign_targets", {
    filter: { campaign_id: campaignId },
    pagination: { page: 1, perPage: 500 },
  });

  const [kind, setKind] = useState<CampaignTaskKind>("visit_demo");
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assigneeSalesId, setAssigneeSalesId] = useState<Identifier | "">("");
  const [typeMetadata, setTypeMetadata] = useState<Record<string, string>>({});
  const [inheritCampaignTargets, setInheritCampaignTargets] = useState(true);
  const [targets, setTargets] = useState<CampaignTargetDraft[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!existing) return;
    setKind(existing.kind);
    setTitle(existing.title);
    setText(existing.text ?? "");
    setDueDate(existing.due_date?.slice(0, 16) ?? "");
    setAssigneeSalesId(existing.assignee_sales_id ?? "");
    setTypeMetadata((existing.type_metadata as Record<string, string>) ?? {});
    setInheritCampaignTargets(existing.inherit_campaign_targets ?? false);
  }, [existing]);

  useEffect(() => {
    if (definitionId == null) return;
    if (existing?.inherit_campaign_targets === false && initialTargets?.length) {
      setTargets(
        initialTargets.map(
          ({ target_type, bc_segment_id, company_id, contact_id, tag_id }) => ({
            target_type,
            bc_segment_id,
            company_id,
            contact_id,
            tag_id,
          }),
        ),
      );
    }
  }, [definitionId, existing?.inherit_campaign_targets, initialTargets]);

  const config = getCampaignTaskTypeConfig(kind);
  const consolidated = isConsolidatedKind(kind);

  const handleSave = async () => {
    if (!title || !dueDate) {
      notify("ra.validation.required", { type: "warning" });
      return;
    }
    if (inheritCampaignTargets) {
      const campaignTargetCount =
        campaignTargets?.length ??
        (await loadCampaignTargets(dataProvider, campaignId)).length;
      if (campaignTargetCount === 0) {
        notify("resources.campaigns.targets.campaign_required", {
          type: "warning",
          message: "Add campaign targets before using inherited targeting",
        });
        return;
      }
    } else if (targets.length === 0) {
      notify("resources.campaigns.targets.required", {
        type: "warning",
        message: "Add at least one target",
      });
      return;
    }
    if (consolidated && !assigneeSalesId) {
      notify("resources.campaigns.assignee.required", {
        type: "warning",
        message: "Select an assignee",
      });
      return;
    }

    setSaving(true);
    try {
      const payload: Partial<CampaignTaskDefinition> = {
        campaign_id: campaignId,
        kind,
        title,
        text,
        due_date: new Date(dueDate).toISOString(),
        assignment_mode: consolidated ? "specific_user" : "related_salesperson",
        assignee_sales_id: consolidated ? assigneeSalesId : null,
        type_metadata: typeMetadata,
        inherit_campaign_targets: inheritCampaignTargets,
      };

      let savedId = definitionId;
      if (definitionId != null) {
        await dataProvider.update("campaign_task_definitions", {
          id: definitionId,
          data: payload,
          previousData: existing,
        });
      } else {
        const { data: created } = await dataProvider.create(
          "campaign_task_definitions",
          { data: payload },
        );
        savedId = assertCreatedId(created, "campaign_task_definitions");
      }

      if (!savedId) {
        throw new Error("Failed to save task definition");
      }

      if (inheritCampaignTargets) {
        await clearDefinitionTargets(dataProvider, savedId);
      } else {
        await replaceDefinitionTargets(dataProvider, savedId, targets);
      }

      if (definitionId != null && existing?.last_generated_at) {
        await (dataProvider as any).reconcileCampaignTasks(definitionId, null);
      }

      notify(
        definitionId != null
          ? "ra.notification.updated"
          : "ra.notification.created",
        { type: "info", messageArgs: { smart_count: 1 } },
      );
      onClose();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {definitionId
              ? translate("resources.campaigns.task_definitions.edit", {
                  _: "Edit task definition",
                })
              : translate("resources.campaigns.task_definitions.add", {
                  _: "Add task definition",
                })}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {translate("resources.campaigns.task_definitions.add", {
              _: "Add task definition",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <label className="text-sm font-medium">
            {translate("resources.campaigns.fields.kind", { _: "Task type" })}
            <select
              className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
              value={kind}
              onChange={(e) => setKind(e.target.value as CampaignTaskKind)}
            >
              {taskTypeChoices.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm font-medium">
            {translate("resources.campaigns.fields.title", { _: "Title" })}
            <input
              className="mt-1 flex h-9 w-full rounded-md border border-input px-3 py-1 text-sm"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>

          <label className="text-sm font-medium">
            {translate("resources.campaigns.fields.instructions", {
              _: "Instructions",
            })}
            <textarea
              className="mt-1 flex min-h-[80px] w-full rounded-md border border-input px-3 py-2 text-sm"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </label>

          <label className="text-sm font-medium">
            {translate("resources.tasks.fields.due_date")}
            <input
              type="datetime-local"
              className="mt-1 flex h-9 w-full rounded-md border border-input px-3 py-1 text-sm"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </label>

          {config?.metadataFields?.map((field) => (
            <label key={field.source} className="text-sm font-medium">
              {getTranslatedCampaignMetadataLabel(
                field.source,
                field.label,
                translate,
              )}
              {field.multiline ? (
                <textarea
                  className="mt-1 flex min-h-[60px] w-full rounded-md border border-input px-3 py-2 text-sm"
                  value={typeMetadata[field.source] ?? ""}
                  onChange={(e) =>
                    setTypeMetadata((m) => ({
                      ...m,
                      [field.source]: e.target.value,
                    }))
                  }
                />
              ) : (
                <input
                  className="mt-1 flex h-9 w-full rounded-md border border-input px-3 py-1 text-sm"
                  value={typeMetadata[field.source] ?? ""}
                  onChange={(e) =>
                    setTypeMetadata((m) => ({
                      ...m,
                      [field.source]: e.target.value,
                    }))
                  }
                />
              )}
            </label>
          ))}

          {consolidated && (
            <label className="text-sm font-medium">
              {translate("resources.campaigns.fields.assignee", {
                _: "Assignee",
              })}
              <select
                className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                value={assigneeSalesId}
                onChange={(e) =>
                  setAssigneeSalesId(
                    e.target.value ? Number(e.target.value) : "",
                  )
                }
              >
                <option value="">Select assignee…</option>
                {salespeople?.map((sale) => (
                  <option key={sale.id} value={sale.id}>
                    {sale.first_name} {sale.last_name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <fieldset className="flex flex-col gap-2 border-0 p-0">
            <legend className="text-sm font-medium mb-1">
              {translate("resources.campaigns.targets.mode", {
                _: "Target mode",
              })}
            </legend>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={inheritCampaignTargets}
                onChange={() => setInheritCampaignTargets(true)}
              />
              {translate("resources.campaigns.targets.inherit", {
                _: "Inherit targets from campaign",
              })}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={!inheritCampaignTargets}
                onChange={() => setInheritCampaignTargets(false)}
              />
              {translate("resources.campaigns.targets.custom", {
                _: "Use custom targets for this definition",
              })}
            </label>
          </fieldset>

          {!inheritCampaignTargets && (
            <CampaignTargetInput targets={targets} onChange={setTargets} />
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {translate("ra.action.cancel")}
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving}>
            {translate("ra.action.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
